<div class="invite-submission-overlay" *ngIf="isSubmitting">
    <div class="invite-submission-overlay__inner">
        <div class="invite-submission-overlay__progress-wrap">
            <mat-progress-bar mode="indeterminate" class="invite-submission-overlay__progress-bar"></mat-progress-bar>
        </div>
        <div class="invite-submission-overlay__message">{{ submissionOverlayMessage }}</div>
        <div class="invite-submission-overlay__subtext" *ngIf="submissionOverlaySubtext">{{ submissionOverlaySubtext }}</div>
    </div>
</div>

<div class="wizard-container">
    <!-- Progress Bar -->
    <div class="progress-container">
        <div class="progress-bar">
            <div class="progress-fill" [style.width.%]="stepProgress"></div>
        </div>
        <div class="progress-text">{{ currentStep + 1 }} of {{ totalSteps }}</div>
    </div>

    <!-- Step Indicators -->
    <div class="step-indicators" *ngIf="!isMobile && !showCompleted">
        <div *ngFor="let step of wizardSteps; let i = index" class="step-indicator" [class.active]="i === currentStep"
            [class.completed]="step.completed" [class.disabled]="!canNavigateToStep(i)"
            [class.clickable]="canNavigateToStep(i)" (click)="canNavigateToStep(i) ? goToStep(i) : null">
            <div class="step-icon">
                <mat-icon>{{ step.icon }}</mat-icon>
            </div>
            <div class="step-title">{{ step.title }}</div>
        </div>
    </div>

    <!-- Main Content -->
    <div class="wizard-content" #scrollableParent>
        <ng-container *ngIf="inviteDetails; else loading">

            <!--
                EVERY STEP BELOW BRANCHES ON `currentStepId`, NEVER ON AN INDEX.

                It used to be `currentStep === 1 && !consentOnly` and so on, which worked only
                because those six steps could never move: nothing was ever spliced in ahead of
                them. `documentOnly` (`tasks/practice-documents.md` §21.2) breaks that — its list
                is `welcome` plus the spliced document steps, so index 1 is the FIRST DOCUMENT and
                `currentStep === 1 && !consentOnly` would have drawn the personal-details form on
                top of it. An id cannot collide with a step that is not in the list.
            -->

            <!-- Welcome Step -->
            <div *ngIf="currentStepId === 'welcome' && !showCompleted" class="step-content welcome-step">
                <div class="welcome-card" *ngIf="!showsAppCard">
                    <div class="welcome-header">
                        <div class="welcome-icon">
                            <mat-icon>medical_services</mat-icon>
                        </div>
                        <h1>Welcome, {{ inviteName }}</h1>
                        <p class="practice-name">{{ group.name }}</p>
                    </div>

                    <div class="welcome-message">
                        <ng-container *ngIf="group.about">
                            <p>{{ group.about }}</p>
                        </ng-container>
                        <ng-container *ngIf="!group.about">
                            <p *ngIf="!consentOnly && !documentOnly">
                                We're committed to providing you with the best possible care. To do this effectively,
                                we need to collect your personal details and medical information. This helps us
                                understand your health needs and provide personalized care.
                            </p>
                            <p *ngIf="consentOnly">
                                We need your explicit consent to proceed with your treatment. Please carefully
                                read through the consent forms and provide your signature to continue.
                            </p>
                            <!-- §21.2: a document-only invite asks for documents and nothing else,
                                 so the welcome must not promise a form the patient will never see. -->
                            <p *ngIf="documentOnly">
                                We need one or more documents back from you. On the next screens you can download
                                each one, complete it, and send it straight back to us — there is nothing else to
                                fill in.
                            </p>
                        </ng-container>
                    </div>

                    <!--
                        WHAT IS NEEDED, BEFORE ANY OF IT IS ASKED FOR — `practice-documents.md` §5.

                        EVERY OUTSTANDING DOCUMENT, not only the blocking ones. It was wired to
                        `requiredInInvite` until 11 Sep 2026, and because that flag is off by
                        default the notice was absent from most invites — which is the very
                        complaint §5 records, arriving again through the setting rather than
                        through the code. See `outstandingDocuments()`.

                        THE HARD BLOCK DID NOT WIDEN WITH IT. The "cannot be completed until"
                        sentence is still about `requiredInInvite` alone, and on a mixed invite it
                        NAMES which documents block, because telling a patient that an optional
                        leaflet will hold up their appointment is its own kind of false.

                        The tier sentences below stay exclusive because their consequences are: a
                        document that fills in on screen costs a patient with no printer nothing,
                        and one that does not costs them the evening. Saying the first about the
                        second is worse than saying nothing.
                    -->
                    <div class="welcome-required-docs" *ngIf="outstandingDocuments().length"
                        data-testid="welcome-required-documents">
                        <mat-icon>assignment_late</mat-icon>
                        <div class="welcome-required-docs__body">
                            <h3>{{ blockingDocuments().length ? 'Before you can finish'
                                : 'What we need from you' }}</h3>
                            <p data-testid="welcome-required-names">
                                We need {{ outstandingDocumentNames() }} back from you.
                            </p>
                            <p *ngIf="blockingDocuments().length"
                                data-testid="welcome-required-blocking">
                                <ng-container *ngIf="everyOutstandingDocumentBlocks()">This form
                                    cannot be completed until we have
                                    {{ outstandingDocuments().length === 1 ? 'it' : 'them' }}.
                                </ng-container>
                                <ng-container *ngIf="!everyOutstandingDocumentBlocks()">This form
                                    cannot be completed until we have
                                    {{ blockingDocumentNames() }}.
                                </ng-container>
                            </p>
                            <p *ngIf="everyOutstandingDocumentFillsOnScreen()"
                                data-testid="welcome-required-onscreen">
                                You can fill
                                {{ outstandingDocuments().length === 1 ? 'it' : 'them' }} in
                                here on your phone — no printer and no scanner needed.
                            </p>
                            <p *ngIf="!everyOutstandingDocumentFillsOnScreen()"
                                data-testid="welcome-required-offline">
                                You will need to download
                                {{ outstandingDocuments().length === 1 ? 'it' : 'them' }}, complete
                                {{ outstandingDocuments().length === 1 ? 'it' : 'them' }}, and send
                                {{ outstandingDocuments().length === 1 ? 'it' : 'them' }} back — a
                                photograph of each page is fine. If you cannot do that right now,
                                your link stays open and you can come back to it.
                            </p>
                        </div>
                    </div>

                    <div class="contact-info"
                        *ngIf="group.practiceNumber || group.practicePhone || group.practiceEmail || group.practiceOpeningTimes">
                        <h3>Practice Information</h3>
                        <div class="contact-grid">
                            <div *ngIf="group.practiceNumber" class="contact-item">
                                <mat-icon>business</mat-icon>
                                <span><strong>Practice Number:</strong> {{ group.practiceNumber }}</span>
                            </div>
                            <div *ngIf="group.practicePhone" class="contact-item">
                                <mat-icon>phone</mat-icon>
                                <span><strong>Phone:</strong> <a href="tel:{{ group.practicePhone }}">{{
                                        group.practicePhone }}</a></span>
                            </div>
                            <div *ngIf="group.practiceEmail" class="contact-item">
                                <mat-icon>email</mat-icon>
                                <span><strong>Email:</strong> <a href="mailto:{{ group.practiceEmail }}">{{
                                        group.practiceEmail }}</a></span>
                            </div>
                            <div *ngIf="group.practiceOpeningTimes" class="contact-item">
                                <mat-icon>schedule</mat-icon>
                                <span><strong>Hours:</strong> {{ group.practiceOpeningTimes }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="wizard-actions center">
                        <button mat-raised-button color="primary" *ngIf="!showsFinishButton"
                            (click)="getStarted()" class="next-btn">
                            Get Started
                            <mat-icon>arrow_forward</mat-icon>
                        </button>
                        <!--
                            A DOCUMENT-ONLY invite with NOTHING LEFT TO RETURN is one step long,
                            and Welcome is that step. Without this the patient stands here with a
                            Get Started button that cannot advance and the invite never reaches
                            `completedAt` — the practice would see it outstanding forever. See
                            `showsFinishButton`.
                        -->
                        <button mat-raised-button color="primary" *ngIf="showsFinishButton"
                            (click)="submit()" class="next-btn" data-testid="document-only-finish">
                            Finish
                            <mat-icon>check</mat-icon>
                        </button>
                    </div>
                </div>

                <!--
                    The patient app (tasks/patient-app.md §15.1). Shown after "Get Started" when the practice opted in.
                    "Continue here" is the wizard exactly as it was; "Open the app" hands the whole invite to the app,
                    where the patient creates an account or signs in and completes it.
                -->
                <div class="welcome-card app-card" *ngIf="showsAppCard" data-testid="patient-app-card">
                    <div class="welcome-header">
                        <div class="welcome-icon">
                            <mat-icon>phone_iphone</mat-icon>
                        </div>
                        <h1>Do this once with the {{ patientAppName }} app</h1>
                        <p class="practice-name">Your details, kept by you, ready for every visit</p>
                    </div>

                    <ul class="app-card__benefits">
                        <li>
                            <mat-icon>bolt</mat-icon>
                            <div><strong>Fill it in once.</strong> Your details are kept in your account, so next time a
                                {{ patientAppName }} practice asks, you only confirm your details.</div>
                        </li>
                        <li>
                            <mat-icon>edit</mat-icon>
                            <div><strong>Keep them up to date.</strong> Change your address, medical aid or allergies in
                                the app and your practices are asked to update their records.</div>
                        </li>
                        <li>
                            <mat-icon>family_restroom</mat-icon>
                            <div><strong>Your family too.</strong> Manage your children's profiles, and invite a partner
                                or another guardian to help.</div>
                        </li>
                        <li>
                            <mat-icon>verified_user</mat-icon>
                            <div><strong>You stay in control.</strong> See what you have shared with each practice, and
                                nothing is shared unless you choose to.</div>
                        </li>
                    </ul>

                    <div class="app-card__actions">
                        <button mat-raised-button color="primary" class="app-card__primary" (click)="useThePatientApp()"
                            [disabled]="appHandoffBusy" data-testid="use-patient-app">
                            <mat-icon>phone_iphone</mat-icon>
                            {{ appHandoffBusy ? 'Opening…' : 'Open the app and create an account' }}
                        </button>
                        <p class="app-card__hint">Already have an account? You can sign in there instead.</p>
                        <p class="app-card__error" *ngIf="appHandoffError" role="alert">{{ appHandoffError }}</p>

                        <div class="app-card__divider"><span>or</span></div>

                        <button mat-stroked-button color="primary" class="app-card__secondary" (click)="nextStep()"
                            [disabled]="appHandoffBusy" data-testid="continue-here">
                            Continue here, just this once
                        </button>
                        <p class="app-card__hint">Nothing is kept for next time, so you may need to fill it in again
                            for a future visit.</p>

                        <button mat-button type="button" class="app-card__back" (click)="showsAppCard = false"
                            [disabled]="appHandoffBusy" data-testid="app-card-back">
                            <mat-icon>arrow_back</mat-icon>
                            Back
                        </button>
                    </div>
                </div>
            </div>

            <!-- Personal Information Step -->
            <div *ngIf="currentStepId === 'personal' && !showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Personal Information</h2>
                        <p>Please provide your basic personal details</p>
                    </div>

                    <form [formGroup]="personalInformationForm" class="wizard-form">
                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Title</mat-label>
                                <mat-select formControlName="title">
                                    <mat-option *ngFor="let title of titles" [value]="title.value">{{ title.text
                                        }}</mat-option>
                                </mat-select>
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>First Name</mat-label>
                                <input matInput formControlName="name" placeholder="Enter your first name">
                            </mat-form-field>


                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Initials</mat-label>
                                <input matInput formControlName="initials" placeholder="Enter your initials">
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Last Name</mat-label>
                                <input matInput formControlName="surname" placeholder="Enter your last name">
                            </mat-form-field>
                        </div>

                        <div class="question-section">
                            <h3>What type of identification do you have?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn" [class.selected]="idTypeSelection === 'sa-id'"
                                    (click)="selectIdTypeOption('sa-id')">
                                    <mat-icon>badge</mat-icon>
                                    <span>I have a SA ID Number</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="idTypeSelection === 'foreign-passport'"
                                    (click)="selectIdTypeOption('foreign-passport')">
                                    <mat-icon>flight_takeoff</mat-icon>
                                    <span>Foreign Passport Number</span>
                                </button>
                            </div>
                            <div *ngIf="!idTypeSelection" class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an identification type</span>
                            </div>
                        </div>

                        <div *ngIf="idTypeSelection" class="conditional-fields">


                            <div class="form-row">
                                <mat-form-field appearance="outline" *ngIf="idTypeSelection === 'sa-id'">
                                    <mat-label>SA ID Number</mat-label>
                                    <input matInput formControlName="idNumber" placeholder="Enter your SA ID number">
                                    <mat-error *ngIf="personalInformationForm.get('idNumber')?.hasError('required')">
                                        ID Number is required.
                                    </mat-error>
                                    <mat-error *ngIf="personalInformationForm.get('idNumber')?.hasError('invalidSaId')">
                                        {{ personalInformationForm.get('idNumber')?.errors?.['invalidSaId'] }}
                                    </mat-error>
                                </mat-form-field>

                                <mat-form-field appearance="outline" *ngIf="idTypeSelection === 'foreign-passport'">
                                    <mat-label>Passport Number</mat-label>
                                    <input matInput formControlName="passportNumber"
                                        placeholder="Enter your passport number">
                                </mat-form-field>


                            </div>

                            <div class="form-row" *ngIf="idTypeSelection === 'foreign-passport'">
                                <mat-form-field appearance="outline">
                                    <mat-label>Passport Country</mat-label>
                                    <mat-select formControlName="passportCountry">
                                        <mat-option *ngFor="let country of countries" [value]="country.value">{{
                                            country.text }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>

                            <div class="form-row">
                                <mat-form-field appearance="outline">
                                    <mat-label>Date of Birth</mat-label>
                                    <input matInput [matDatepicker]="dobPicker" formControlName="dob"
                                        placeholder="DD/MM/YYYY" [max]="todayDate">
                                    <mat-datepicker-toggle matSuffix [for]="dobPicker"></mat-datepicker-toggle>
                                    <mat-datepicker #dobPicker></mat-datepicker>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                    <mat-label>Gender</mat-label>
                                    <mat-select formControlName="gender">
                                        <mat-option value="Male">Male</mat-option>
                                        <mat-option value="Female">Female</mat-option>
                                        <mat-option value="Other">Other</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>





                        <div class="wizard-actions">
                            <button mat-button (click)="previousStep()" class="back-btn">
                                <mat-icon>arrow_back</mat-icon>
                                Back
                            </button>
                            <span class="step-blocked-note" *ngIf="!isStepValid(1)">
                                {{ incompleteStepMessage }}
                            </span>
                            <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn"
                                [disabled]="!isStepValid(1)">
                                Next
                                <mat-icon>arrow_forward</mat-icon>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Contact Information Step -->
            <div *ngIf="currentStepId === 'contact' && !showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Contact Information</h2>
                        <p>How can we reach you?</p>
                    </div>

                    <form [formGroup]="contactInformationForm" class="wizard-form">
                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Email Address</mat-label>
                                <input matInput formControlName="email" type="email"
                                    placeholder="your.email@example.com">
                            </mat-form-field>
                        </div>

                        <div class="form-row">
                            <app-intl-phone-input [preferredCountries]="['za']"
                                [providedValue]="contactInformationForm.get('mobileNumber')?.value"
                                placeholder="Mobile Number" [required]="true"
                                (valueChange)="contactInformationForm.patchValue({mobileNumber:$event})"
                                name="mobileNumber" classList="full-width">
                            </app-intl-phone-input>

                            <app-intl-phone-input [preferredCountries]="['za']"
                                [providedValue]="contactInformationForm.get('phoneNumber')?.value"
                                placeholder="Home Phone (Optional)"
                                (valueChange)="contactInformationForm.patchValue({phoneNumber:$event})"
                                name="phoneNumber" classList="full-width">
                            </app-intl-phone-input>
                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Home Address</mat-label>
                                <textarea matInput cdkTextareaAutosize="true" formControlName="address"
                                    placeholder="Enter your full address"></textarea>
                            </mat-form-field>
                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Postal Code</mat-label>
                                <input matInput formControlName="addressCode" type="number" placeholder="e.g., 2000">
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Religion</mat-label>
                                <input matInput formControlName="religion" placeholder="e.g., Christian, Muslim">
                            </mat-form-field>
                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Home Language</mat-label>
                                <input matInput formControlName="language" placeholder="e.g., English, Afrikaans">
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Occupation</mat-label>
                                <input matInput formControlName="occupation" placeholder="e.g., Teacher">
                            </mat-form-field>
                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Employer</mat-label>
                                <input matInput formControlName="employer" placeholder="e.g., Company Name">
                            </mat-form-field>

                            <app-intl-phone-input [preferredCountries]="['za']"
                                [providedValue]="contactInformationForm.get('workPhone')?.value"
                                placeholder="Work Phone"
                                (valueChange)="contactInformationForm.patchValue({workPhone:$event})" name="workPhone"
                                classList="full-width">
                            </app-intl-phone-input>
                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Work Address</mat-label>
                                <textarea matInput cdkTextareaAutosize="true" formControlName="workAddress"
                                    placeholder="Enter your work address"></textarea>
                            </mat-form-field>
                        </div>

                        <div class="form-row">
                            <mat-form-field appearance="outline">
                                <mat-label>Work Postal Code</mat-label>
                                <input matInput formControlName="workAddressCode" type="number"
                                    placeholder="e.g., 2000">
                            </mat-form-field>

                            <mat-form-field appearance="outline">
                                <mat-label>Marital Status</mat-label>
                                <input matInput formControlName="maritalStatus" placeholder="e.g., Single, Married">
                            </mat-form-field>
                        </div>

                        <div class="wizard-actions">
                            <button mat-button (click)="previousStep()" class="back-btn">
                                <mat-icon>arrow_back</mat-icon>
                                Back
                            </button>
                            <span class="step-blocked-note" *ngIf="!isStepValid(2)">
                                {{ incompleteStepMessage }}
                            </span>
                            <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn"
                                [disabled]="!isStepValid(2)">
                                Next
                                <mat-icon>arrow_forward</mat-icon>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Account Holder Step -->
            <div *ngIf="currentStepId === 'account' && !showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Medical Aid & Account Details</h2>
                        <p>Medical insurance information and account responsibility</p>
                    </div>

                    <form [formGroup]="personLiableForm" class="wizard-form">
                        <!-- Medical Aid Information Section -->
                        <div class="question-section">
                            <h3>Do you have medical aid or health insurance?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn"
                                    [class.selected]="personLiableForm.get('hasMedicalAid')?.value === true"
                                    (click)="selectMedicalAidOption(true)">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Yes, I have medical aid</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="personLiableForm.get('hasMedicalAid')?.value === false"
                                    (click)="selectMedicalAidOption(false)">
                                    <mat-icon>cancel</mat-icon>
                                    <span>No, private patient</span>
                                </button>
                            </div>
                            <div *ngIf="personLiableForm.get('hasMedicalAid')?.value === null"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for medical aid</span>
                            </div>
                        </div>

                        <div *ngIf="personLiableForm.get('hasMedicalAid')?.value === true" class="conditional-fields">
                            <div class="section-card">
                                <h3>Medical Aid Information</h3>
                                <app-medical-aid-picker [schemes]="medicalAidSchemes" [initial]="medicalAidInitial"
                                    label="Your medical aid" (pickChange)="onMedicalAidPick($event)">
                                </app-medical-aid-picker>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Medical Aid Number</mat-label>
                                        <input matInput formControlName="medicalAidNumber"
                                            placeholder="Enter medical aid number">
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Dependant Code</mat-label>
                                        <input matInput formControlName="medicalDependantCode" placeholder="e.g., 01">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Date Joined</mat-label>
                                        <input matInput [matDatepicker]="medicalDateJoinedPicker"
                                            formControlName="medicalDateJoined" placeholder="DD/MM/YYYY"
                                            [max]="todayDate">
                                        <mat-datepicker-toggle matSuffix
                                            [for]="medicalDateJoinedPicker"></mat-datepicker-toggle>
                                        <mat-datepicker #medicalDateJoinedPicker></mat-datepicker>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Main Member</mat-label>
                                        <input matInput formControlName="medicalMainMember"
                                            placeholder="Enter main member name">
                                    </mat-form-field>
                                </div>


                            </div>
                        </div>

                        <!-- Gap Cover Section (only shows if medical aid is selected) -->
                        <div *ngIf="personLiableForm.get('hasMedicalAid')?.value === true" class="question-section">
                            <h3>Do you have gap cover?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn"
                                    [class.selected]="personLiableForm.get('hasGapCover')?.value === true"
                                    (click)="selectGapCoverOption(true)">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Yes, I have gap cover</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="personLiableForm.get('hasGapCover')?.value === false"
                                    (click)="selectGapCoverOption(false)">
                                    <mat-icon>cancel</mat-icon>
                                    <span>No, I don't have gap cover</span>
                                </button>
                            </div>
                            <div *ngIf="personLiableForm.get('hasGapCover')?.value === null" class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for gap cover</span>
                            </div>
                        </div>

                        <div *ngIf="personLiableForm.get('hasMedicalAid')?.value === true && personLiableForm.get('hasGapCover')?.value === true"
                            class="conditional-fields">
                            <div class="section-card">
                                <h3>Gap Cover Information</h3>
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Gap Cover Name</mat-label>
                                        <input matInput formControlName="gapCover"
                                            placeholder="e.g., Gap Cover Provider">
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Gap Cover Policy Number</mat-label>
                                        <input matInput formControlName="gapCoverNumber"
                                            placeholder="Enter gap cover policy number">
                                    </mat-form-field>
                                </div>
                            </div>
                        </div>

                        <div class="question-section">
                            <h3>Are you the main member and responsible for the account?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn" [class.selected]="accountHolderSame === true"
                                    (click)="selectAccountHolderOption(true)">
                                    <mat-icon>person</mat-icon>
                                    <span>Yes, I am</span>
                                </button>
                                <button type="button" class="option-btn" [class.selected]="accountHolderSame === false"
                                    (click)="selectAccountHolderOption(false)">
                                    <mat-icon>person_add</mat-icon>
                                    <span>No, I am not</span>
                                </button>
                            </div>
                            <div *ngIf="accountHolderSame === null" class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for account responsibility</span>
                            </div>
                        </div>

                        <div *ngIf="accountHolderSame === false" class="conditional-fields">
                            <!-- Personal Information Section -->
                            <div class="section-card">
                                <h3>Personal Information</h3>
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Title</mat-label>
                                        <mat-select formControlName="liableTitle">
                                            <mat-option *ngFor="let title of titles" [value]="title.value">{{ title.text
                                                }}</mat-option>
                                        </mat-select>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>First Name</mat-label>
                                        <input matInput formControlName="liableName" placeholder="Enter first name">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Initials</mat-label>
                                        <input matInput formControlName="liableInitials" placeholder="Auto-calculated"
                                            readonly>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Last Name</mat-label>
                                        <input matInput formControlName="liableSurname" placeholder="Enter last name">
                                    </mat-form-field>
                                </div>

                                <div class="question-section">
                                    <h4>What type of identification does the account holder have?</h4>
                                    <div class="option-buttons">
                                        <button type="button" class="option-btn"
                                            [class.selected]="liableIdTypeSelection === 'sa-id'"
                                            (click)="selectLiableIdTypeOption('sa-id')">
                                            <mat-icon>badge</mat-icon>
                                            <span>SA ID Number</span>
                                        </button>
                                        <button type="button" class="option-btn"
                                            [class.selected]="liableIdTypeSelection === 'foreign-passport'"
                                            (click)="selectLiableIdTypeOption('foreign-passport')">
                                            <mat-icon>flight_takeoff</mat-icon>
                                            <span>Foreign Passport Number</span>
                                        </button>
                                    </div>
                                    <div *ngIf="!liableIdTypeSelection" class="validation-message">
                                        <mat-icon>error</mat-icon>
                                        <span>Please select an identification type for the account holder</span>
                                    </div>
                                </div>

                                <div *ngIf="liableIdTypeSelection" class="conditional-fields">


                                    <div class="form-row">
                                        <mat-form-field appearance="outline" *ngIf="liableIdTypeSelection === 'sa-id'">
                                            <mat-label>SA ID Number</mat-label>
                                            <input matInput formControlName="liableIdNumber"
                                                placeholder="Enter SA ID number">
                                            <mat-error
                                                *ngIf="personLiableForm.get('liableIdNumber')?.hasError('invalidSaId')">
                                                {{ personLiableForm.get('liableIdNumber')?.errors?.['invalidSaId'] }}
                                            </mat-error>
                                        </mat-form-field>

                                        <mat-form-field appearance="outline"
                                            *ngIf="liableIdTypeSelection === 'foreign-passport'">
                                            <mat-label>Passport Number</mat-label>
                                            <input matInput formControlName="liablePassportNumber"
                                                placeholder="Enter passport number">
                                        </mat-form-field>

                                    </div>


                                    <div class="form-row" *ngIf="liableIdTypeSelection === 'foreign-passport'">
                                        <mat-form-field appearance="outline">
                                            <mat-label>Passport Country</mat-label>
                                            <mat-select formControlName="liablePassportCountry">
                                                <mat-option *ngFor="let country of countries" [value]="country.value">{{
                                                    country.text }}</mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                    </div>

                                </div>
                            </div>

                            <!-- Contact Information Section -->
                            <div class="section-card">
                                <h3>Contact Information</h3>
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Email Address</mat-label>
                                        <input matInput formControlName="liableEmail" type="email"
                                            placeholder="email@example.com">
                                    </mat-form-field>

                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="personLiableForm.get('liableMobileNumber')?.value"
                                        placeholder="Mobile Number"
                                        (valueChange)="personLiableForm.patchValue({liableMobileNumber:$event})"
                                        name="liableMobileNumber" classList="full-width">
                                    </app-intl-phone-input>
                                </div>

                                <div class="form-row">
                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="personLiableForm.get('liablePhoneNumber')?.value"
                                        placeholder="Home Phone"
                                        (valueChange)="personLiableForm.patchValue({liablePhoneNumber:$event})"
                                        name="liablePhoneNumber" classList="full-width">
                                    </app-intl-phone-input>
                                </div>
                            </div>

                            <!-- Address Information Section -->
                            <div class="section-card">
                                <h3>Address Information</h3>
                                <div class="form-row">
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>Home Address</mat-label>
                                        <textarea matInput cdkTextareaAutosize="true" formControlName="liableAddress"
                                            placeholder="Enter home address"></textarea>
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Postal Code</mat-label>
                                        <input matInput formControlName="liableAddressCode" type="number"
                                            placeholder="e.g., 2000">
                                    </mat-form-field>
                                </div>
                            </div>

                            <!-- Employment Information Section -->
                            <div class="section-card">
                                <h3>Employment Information</h3>
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Occupation</mat-label>
                                        <input matInput formControlName="liableOccupation" placeholder="e.g., Teacher">
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Employer</mat-label>
                                        <input matInput formControlName="liableEmployer"
                                            placeholder="e.g., Company Name">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="personLiableForm.get('liableWorkPhone')?.value"
                                        placeholder="Work Phone"
                                        (valueChange)="personLiableForm.patchValue({liableWorkPhone:$event})"
                                        name="liableWorkPhone" classList="full-width">
                                    </app-intl-phone-input>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>Work Address</mat-label>
                                        <textarea matInput cdkTextareaAutosize="true"
                                            formControlName="liableWorkAddress"
                                            placeholder="Enter work address"></textarea>
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Work Postal Code</mat-label>
                                        <input matInput formControlName="liableWorkAddressCode" type="number"
                                            placeholder="e.g., 2000">
                                    </mat-form-field>
                                </div>
                            </div>
                        </div>

                        <div class="wizard-actions">
                            <button mat-button (click)="previousStep()" class="back-btn">
                                <mat-icon>arrow_back</mat-icon>
                                Back
                            </button>
                            <span class="step-blocked-note" *ngIf="!isStepValid(3)">
                                {{ incompleteStepMessage }}
                            </span>
                            <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn"
                                [disabled]="!isStepValid(3)">
                                Next
                                <mat-icon>arrow_forward</mat-icon>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Medical Details Step -->
            <div *ngIf="currentStepId === 'medical' && !showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Medical Information</h2>
                        <p>Help us understand your medical background</p>
                    </div>

                    <form [formGroup]="medicalInsuranceForm" class="wizard-form">

                        <div class="section-card">
                            <h3>Physical Measurements</h3>
                            <div class="form-row">
                                <mat-form-field appearance="outline">
                                    <mat-label>Height</mat-label>
                                    <input matInput formControlName="height" type="number"
                                        placeholder="Enter height in cm (e.g. 180)" heightInput>
                                    <span matSuffix class="m-r-5">cm</span>
                                </mat-form-field>

                                <mat-form-field appearance="outline">
                                    <mat-label>Weight</mat-label>
                                    <input matInput formControlName="weight" type="number"
                                        placeholder="Enter weight in kg (e.g. 70)" weightInput>
                                    <span matSuffix class="m-r-5">kg</span>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="section-card">
                            <h3>Reason for Visit</h3>
                            <p class="section-guidance">Please briefly describe the reason for your visit to the doctor (optional, maximum 20 words).</p>
                            <div class="form-row">
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Reason for visiting the doctor</mat-label>
                                    <textarea matInput cdkTextareaAutosize="true" formControlName="reasonForVisiting"
                                        placeholder="e.g., Annual checkup, chest pain, follow-up appointment"
                                        [maxlength]="500"></textarea>
                                    <mat-hint align="end">
                                        <span [class.word-count-warning]="getWordCount(medicalInsuranceForm.get('reasonForVisiting')?.value || '') > 20">
                                            {{ getWordCount(medicalInsuranceForm.get('reasonForVisiting')?.value || '') }}/20 words
                                        </span>
                                    </mat-hint>
                                    <mat-error *ngIf="medicalInsuranceForm.get('reasonForVisiting')?.hasError('wordCountExceeded')">
                                        {{ medicalInsuranceForm.get('reasonForVisiting')?.errors?.['wordCountExceeded']?.message }}
                                    </mat-error>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="section-card">
                            <h3>My Medical Team</h3>
                            <p class="section-guidance">This information is used to communicate with your other health
                                professionals. Please list your primary care physician, specialists, therapists, or any
                                other healthcare providers who should be kept informed about your care.</p>
                            <app-medical-team-list-creator 
                                [medicalTeam]="medicalTeamForm.get('medicalTeam')?.value || []"
                                (teamChanged)="onMedicalTeamChanged($event)">
                            </app-medical-team-list-creator>
                        </div>

                        <div class="question-section">
                            <h3>Do you have any allergies or medical alerts we should be aware of?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn"
                                    [class.selected]="allergyForm.get('noAllergies')?.value === false"
                                    (click)="selectAllergiesOption(true)">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Yes, I have allergies</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="allergyForm.get('noAllergies')?.value === true"
                                    (click)="selectAllergiesOption(false)">
                                    <mat-icon>cancel</mat-icon>
                                    <span>No, I have no allergies</span>
                                </button>
                            </div>
                            <div *ngIf="allergyForm.get('noAllergies')?.value === null" class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for allergies</span>
                            </div>
                        </div>
                        <div *ngIf="allergyForm.get('noAllergies')?.value === false" class="conditional-fields">
                            <div class="section-card">
                                <h3>Allergies & Alerts</h3>
                                <p class="section-guidance">Please list any allergies to medications, foods,
                                    environmental factors, or other substances. Include the severity of reactions and
                                    any medical alerts that healthcare providers should be aware of.</p>
                                <app-allergy-list-creator 
                                    [allergies]="allergyForm.get('allergies')?.value || []"
                                    [noAllergies]="allergyForm.get('noAllergies')?.value || false"
                                    (allergiesChanged)="onAllergiesChanged($event)"
                                    (noAllergiesChanged)="onNoAllergiesChanged($event)" 
                                    [hideCheckbox]="true">
                                </app-allergy-list-creator>
                            </div>
                            <div *ngIf="allergyForm.get('noAllergies')?.value === false && allergyForm.get('allergies')?.value?.length === 0"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please add at least one allergy or medical alert</span>
                            </div>
                        </div>

                        <div class="question-section">
                            <h3>Have you had any previous medical conditions, surgeries, or operations?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn"
                                    [class.selected]="medicalHistoryForm.get('noMedicalHistory')?.value === false"
                                    (click)="selectMedicalHistoryOption(true)">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Yes, I have medical history</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="medicalHistoryForm.get('noMedicalHistory')?.value === true"
                                    (click)="selectMedicalHistoryOption(false)">
                                    <mat-icon>cancel</mat-icon>
                                    <span>No, I have no medical history</span>
                                </button>
                            </div>
                            <div *ngIf="medicalHistoryForm.get('noMedicalHistory')?.value === null"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for medical history</span>
                            </div>
                        </div>
                        <div *ngIf="medicalHistoryForm.get('noMedicalHistory')?.value === false"
                            class="conditional-fields">
                            <div class="section-card">
                                <h3>Medical History & Operations</h3>
                                <p class="section-guidance">Please include any significant medical conditions,
                                    surgeries, hospitalizations, or procedures you have had. Include dates when possible
                                    and any ongoing conditions that require monitoring or treatment.</p>
                                <app-medical-history-list-creator 
                                    [history]="medicalHistoryForm.get('medicalHistory')?.value || []"
                                    [noHistory]="medicalHistoryForm.get('noMedicalHistory')?.value || false"
                                    (historyChanged)="onMedicalHistoryChanged($event)"
                                    (noHistoryChanged)="onNoMedicalHistoryChanged($event)" 
                                    [hideCheckbox]="true" 
                                    [showCommonConditionsByDefault]="true">
                                </app-medical-history-list-creator>
                            </div>
                            <div *ngIf="medicalHistoryForm.get('noMedicalHistory')?.value === false && medicalHistoryForm.get('medicalHistory')?.value?.length === 0"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please add at least one medical history item or operation</span>
                            </div>
                        </div>

                        <div class="question-section">
                            <h3>Are you currently taking any medications, supplements, or treatments?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn"
                                    [class.selected]="medicationHistoryForm.get('noMedicationHistory')?.value === false"
                                    (click)="selectMedicationsOption(true)">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Yes, I take medications</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="medicationHistoryForm.get('noMedicationHistory')?.value === true"
                                    (click)="selectMedicationsOption(false)">
                                    <mat-icon>cancel</mat-icon>
                                    <span>No, I don't take medications</span>
                                </button>
                            </div>
                            <div *ngIf="medicationHistoryForm.get('noMedicationHistory')?.value === null"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for medications</span>
                            </div>
                        </div>
                        <div *ngIf="medicationHistoryForm.get('noMedicationHistory')?.value === false"
                            class="conditional-fields">
                            <div class="section-card">
                                <h3>Current Medications</h3>
                                <p class="section-guidance">Please list all medications you are currently taking,
                                    including prescription drugs, over-the-counter medications, vitamins, supplements,
                                    and herbal remedies. Include dosage and frequency when possible.</p>
                                <app-medication-list-creator 
                                    [medications]="medicationHistoryForm.get('medicationHistory')?.value || []"
                                    [noMedication]="medicationHistoryForm.get('noMedicationHistory')?.value || false"
                                    (medicationChanged)="onMedicationChanged($event)"
                                    (noMedicationChanged)="onNoMedicationChanged($event)" 
                                    [hideCheckbox]="true">
                                </app-medication-list-creator>
                            </div>
                            <div *ngIf="medicationHistoryForm.get('noMedicationHistory')?.value === false && medicationHistoryForm.get('medicationHistory')?.value?.length === 0"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please add at least one medication, supplement, or treatment</span>
                            </div>
                        </div>

                        <div class="wizard-actions">
                            <button mat-button (click)="previousStep()" class="back-btn">
                                <mat-icon>arrow_back</mat-icon>
                                Back
                            </button>
                            <span class="step-blocked-note" *ngIf="!isStepValid(4)">
                                {{ incompleteStepMessage }}
                            </span>
                            <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn"
                                [disabled]="!isStepValid(4)">
                                Next
                                <mat-icon>arrow_forward</mat-icon>
                            </button>
                        </div>
                    </form>
                </div>
            </div>



            <!-- Emergency Contact Step -->
            <div *ngIf="currentStepId === 'emergency' && !showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Emergency Contact</h2>
                        <p>Who should we contact in case of emergency?</p>
                    </div>

                    <form [formGroup]="emergencyContactForm" class="wizard-form">
                        <div class="question-section" *ngIf="!(group?.enforcedInviteSections?.nextOfKin === true)">
                            <h3>Would you like to provide emergency contact or next of kin information?</h3>
                            <div class="option-buttons">
                                <button type="button" class="option-btn"
                                    [class.selected]="emergencyContactForm.get('hasEmergencyContact')?.value === true"
                                    (click)="selectEmergencyContactOption(true)">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Yes, I want to provide contacts</span>
                                </button>
                                <button type="button" class="option-btn"
                                    [class.selected]="emergencyContactForm.get('hasEmergencyContact')?.value === false"
                                    (click)="selectEmergencyContactOption(false)">
                                    <mat-icon>cancel</mat-icon>
                                    <span>No, I don't want to provide contacts</span>
                                </button>
                            </div>
                            <div *ngIf="emergencyContactForm.get('hasEmergencyContact')?.value === null"
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>Please select an option for emergency contact information</span>
                            </div>
                        </div>

                        <div *ngIf="emergencyContactForm.get('hasEmergencyContact')?.value === true || group?.enforcedInviteSections?.nextOfKin === true"
                            class="conditional-fields">
                            <div class="instruction-text">
                                <p><strong>Note:</strong> You must provide at least one emergency contact with a full name and either a mobile number or email address. You can fill in either the primary or secondary contact (or both).</p>
                            </div>
                            <div class="emergency-contact">
                                <h3>Primary Emergency Contact</h3>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Full Name</mat-label>
                                        <input matInput formControlName="emergencyContact"
                                            placeholder="Enter full name">
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Relationship</mat-label>
                                        <input matInput formControlName="emergencyContactRelationship"
                                            placeholder="e.g., Spouse, Parent">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="emergencyContactForm.get('emergencyContactMobile')?.value"
                                        placeholder="Mobile Number"
                                        (valueChange)="emergencyContactForm.patchValue({emergencyContactMobile:$event})"
                                        name="emergencyContactMobile" classList="full-width">
                                    </app-intl-phone-input>

                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="emergencyContactForm.get('emergencyContactPhone')?.value"
                                        placeholder="Home Phone"
                                        (valueChange)="emergencyContactForm.patchValue({emergencyContactPhone:$event})"
                                        name="emergencyContactPhone" classList="full-width">
                                    </app-intl-phone-input>
                                </div>

                                <div class="form-row">
                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="emergencyContactForm.get('emergencyContactWork')?.value"
                                        placeholder="Work Phone"
                                        (valueChange)="emergencyContactForm.patchValue({emergencyContactWork:$event})"
                                        name="emergencyContactWork" classList="full-width">
                                    </app-intl-phone-input>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Email Address</mat-label>
                                        <input matInput formControlName="emergencyContactEmail" type="email"
                                            placeholder="email@example.com">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>Address</mat-label>
                                        <textarea matInput cdkTextareaAutosize="true"
                                            formControlName="emergencyContactAddress"
                                            placeholder="Enter emergency contact address"></textarea>
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Postal Code</mat-label>
                                        <input matInput formControlName="emergencyContactAddressCode" type="number"
                                            placeholder="e.g., 2000">
                                    </mat-form-field>
                                </div>
                            </div>

                            <div class="emergency-contact">
                                <h3>Secondary Emergency Contact</h3>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Full Name</mat-label>
                                        <input matInput formControlName="emergencyContact2"
                                            placeholder="Enter full name">
                                    </mat-form-field>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Relationship</mat-label>
                                        <input matInput formControlName="emergencyContactRelationship2"
                                            placeholder="e.g., Spouse, Parent">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="emergencyContactForm.get('emergencyContactMobile2')?.value"
                                        placeholder="Mobile Number"
                                        (valueChange)="emergencyContactForm.patchValue({emergencyContactMobile2:$event})"
                                        name="emergencyContactMobile2" classList="full-width">
                                    </app-intl-phone-input>

                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="emergencyContactForm.get('emergencyContactPhone2')?.value"
                                        placeholder="Home Phone"
                                        (valueChange)="emergencyContactForm.patchValue({emergencyContactPhone2:$event})"
                                        name="emergencyContactPhone2" classList="full-width">
                                    </app-intl-phone-input>
                                </div>

                                <div class="form-row">
                                    <app-intl-phone-input [preferredCountries]="['za']"
                                        [providedValue]="emergencyContactForm.get('emergencyContactWork2')?.value"
                                        placeholder="Work Phone"
                                        (valueChange)="emergencyContactForm.patchValue({emergencyContactWork2:$event})"
                                        name="emergencyContactWork2" classList="full-width">
                                    </app-intl-phone-input>

                                    <mat-form-field appearance="outline">
                                        <mat-label>Email Address</mat-label>
                                        <input matInput formControlName="emergencyContactEmail2" type="email"
                                            placeholder="email@example.com">
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>Address</mat-label>
                                        <textarea matInput cdkTextareaAutosize="true"
                                            formControlName="emergencyContactAddress2"
                                            placeholder="Enter emergency contact address"></textarea>
                                    </mat-form-field>
                                </div>

                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Postal Code</mat-label>
                                        <input matInput formControlName="emergencyContactAddressCode2" type="number"
                                            placeholder="e.g., 2000">
                                    </mat-form-field>
                                </div>
                            </div>
                            
                            <!-- Emergency contact validation error message -->
                            <div *ngIf="emergencyContactForm.errors?.['emergencyContactRequired']" 
                                class="validation-message">
                                <mat-icon>error</mat-icon>
                                <span>{{ emergencyContactForm.errors?.['emergencyContactRequired']?.message }}</span>
                            </div>
                        </div>

                        <div class="wizard-actions">
                            <button mat-button (click)="previousStep()" class="back-btn">
                                <mat-icon>arrow_back</mat-icon>
                                Back
                            </button>
                            <span class="step-blocked-note" *ngIf="!isStepValid(5)">
                                {{ incompleteStepMessage }}
                            </span>
                            <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn"
                                [disabled]="!isStepValid(5)">
                                Next
                                <mat-icon>arrow_forward</mat-icon>
                            </button>
                        </div>
                    </form>
                </div>
            </div>



            <!-- Consent Step -->
            <div *ngIf="currentStepId === 'consent' && !showCompleted"
                class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Consent Forms</h2>
                        <p>Please read and accept the consent forms</p>
                    </div>

                    <div class="consent-forms">
                        <form [formGroup]="consentForm" *ngFor="let consentForm of consentFormGroups; let i = index"
                            class="consent-form">
                            <div class="consent-header">
                                <h3>{{ consentForm.get('consentLabel')?.value }}</h3>
                                <div class="consent-actions">
                                    <button mat-icon-button matTooltip="View fullscreen"
                                        (click)="viewConsentFullscreen(consentForm)">
                                        <mat-icon>fullscreen</mat-icon>
                                    </button>
                                    <button mat-icon-button matTooltip="Download PDF"
                                        (click)="downloadConsentPDF(consentForm)">
                                        <mat-icon>download</mat-icon>
                                    </button>
                                </div>
                            </div>

                            <div class="consent-text-container">
                                <pre [innerHtml]="consentForm.get('consentText')?.value" class="consent-text"></pre>
                            </div>

                            <ng-container
                                *ngIf="consentForm.get('confirmSide')?.value && consentForm.get('sideOptions')?.value?.length">
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Procedure Side</mat-label>
                                    <mat-select formControlName="selectedProcedureSide" required>
                                        <mat-option *ngFor="let option of consentForm.get('sideOptions')?.value"
                                            [value]="option">
                                            {{ option }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </ng-container>

                            <ng-container
                                *ngIf="consentForm.get('confirmImplant')?.value && consentForm.get('implantOptions')?.value?.length">
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Implant</mat-label>
                                    <mat-select formControlName="selectedImplant" required>
                                        <mat-option *ngFor="let option of consentForm.get('implantOptions')?.value"
                                            [value]="option">
                                            {{ option }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </ng-container>

                            <ng-container
                                *ngIf="consentForm.get('confirmTransfusion')?.value && consentForm.get('transfusionOptions')?.value?.length">
                                <mat-form-field appearance="outline" class="full-width">
                                    <mat-label>Blood Transfusion</mat-label>
                                    <mat-select formControlName="selectedTransfusion" required>
                                        <mat-option *ngFor="let option of consentForm.get('transfusionOptions')?.value"
                                            [value]="option">
                                            {{ option }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </ng-container>

                            <ng-container *ngIf="consentForm.get('additionalRequiredConsent')?.value?.length">
                                <h3>REQUIRED CONSENT</h3>
                                <p class="align-left"><strong>I Consent to the following:</strong></p>
                                <mat-radio-group formControlName="selectedRequiredConsent">
                                    <mat-radio-button
                                        *ngFor="let consent of consentForm.get('additionalRequiredConsent')?.value"
                                        [value]="consent">
                                        {{ consent }}
                                    </mat-radio-button>
                                </mat-radio-group>
                            </ng-container>

                            <ng-container *ngIf="consentForm.get('conditionalConsent')?.value?.length">
                                <h3>OPTIONAL CONSENT</h3>
                                <p class="align-left"><strong>I Consent to the following:</strong></p>
                                <div formArrayName="conditionalConsent">
                                    <div *ngFor="let conditional of getConditionalConsentArray(consentForm).controls; let i = index"
                                        [formGroupName]="i">
                                        <mat-checkbox formControlName="consentGiven">{{
                                            conditional.get('name')?.value }}</mat-checkbox>
                                    </div>
                                </div>
                            </ng-container>

                            <ng-container *ngIf="consentForm.get('form')?.value.consentType === 'interactive'">
                                <p class="p-t-10 align-left">Based on the information and statements provided, I
                                    acknowledge that my doctor has thoroughly explained the procedure to
                                    me, answered all my questions, and I hereby request and consent to the
                                    treatment.</p>
                            </ng-container>

                            <div class="consent-checkbox"
                                [class.consent-outstanding]="!consentForm.get('consentGiven')?.value">
                                <mat-checkbox formControlName="consentGiven" color="primary">
                                    I have read and agree to the terms
                                </mat-checkbox>
                            </div>
                        </form>
                    </div>

                    <div class="wizard-actions">
                        <button mat-button (click)="previousStep()" class="back-btn">
                            <mat-icon>arrow_back</mat-icon>
                            Back
                        </button>
                        <!-- `currentStep`, not a literal 6: practice forms splice steps in after
                             this one, and this block is also the consent-only invite's step 1. -->
                        <span class="step-blocked-note" *ngIf="!isStepValid(currentStep)">
                            {{ incompleteStepMessage }}
                        </span>
                        <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn"
                            [disabled]="!isStepValid(currentStep)">
                            Next
                            <mat-icon>arrow_forward</mat-icon>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Practice form steps - `tasks/patient-forms.md` §6, one step per visible SECTION.
                 Spliced between Consent and Signature by `applyFormSteps()`.

                 `[hidden]`, NEVER `*ngIf`: destroying a renderer takes its controls with it, and
                 with them every answer the patient has already given on that form's other
                 sections. The renderer computes visibility, the prune and the answers map over
                 the WHOLE schema regardless of which section it is paging to, so one instance per
                 FORM (not per step) is both correct and the only shape that keeps the answers. -->
            <div *ngFor="let formRequest of formRequests; trackBy: trackFormRequest"
                [hidden]="!isFormStepVisible(formRequest) || showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>{{ formRequest.name }}</h2>
                        <p *ngIf="formRequest.description">{{ formRequest.description }}</p>
                        <p *ngIf="!formRequest.description">Please answer the questions below</p>
                    </div>

                    <app-dynamic-form-renderer [schema]="formRequest.schema" [value]="formRequest.answers"
                        [sectionId]="formRequest.activeSectionId"
                        (valueChange)="onFormAnswers(formRequest, $event)"
                        (sectionsChange)="onFormSections(formRequest, $event)">
                    </app-dynamic-form-renderer>

                    <div class="wizard-actions">
                        <button mat-button (click)="previousStep()" class="back-btn">
                            <mat-icon>arrow_back</mat-icon>
                            Back
                        </button>
                        <span class="step-blocked-note" *ngIf="!isStepValid(currentStep)">
                            {{ incompleteStepMessage }}
                        </span>
                        <button mat-raised-button color="primary" (click)="nextStep()" class="next-btn">
                            Next
                            <mat-icon>arrow_forward</mat-icon>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Practice document steps - `tasks/practice-documents.md` §19, one step per document.
                 Spliced between the form steps and Signature by `applyDocumentSteps()`.

                 THERE IS NO EMPTINESS GUARD HERE AND THERE MUST NOT BE ONE. An invite carrying no
                 documents has no `documentRequests`, so this `*ngFor` draws nothing and no step
                 exists to navigate to — the visibility is structural, exactly as it is for the form
                 steps above. An `*ngIf="documentRequests.length"` wrapper would be a second opinion
                 about the same fact, and the consent step's hardcoded entry (which renders an empty
                 screen when nothing is outstanding) is the failure being avoided.

                 `[hidden]`, NEVER `*ngIf`: destroying this block mid-upload would take the in-flight
                 return and the patient's chosen files with it, and a patient who steps back to check
                 something WILL do that. -->
            <div *ngFor="let documentRequest of documentRequests; trackBy: trackDocumentRequest"
                [hidden]="!isDocumentStepVisible(documentRequest) || showCompleted" class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>{{ documentRequest.name }}</h2>
                        <p *ngIf="documentRequest.description">{{ documentRequest.description }}</p>
                        <!--
                            THE DEFAULT SENTENCE IS WRONG FOR A RESUBMISSION.

                            "There are two steps: download the document, then send the completed
                            pages back here" is exactly what somebody who has already done both
                            things does not need to be told - and reads as though the practice lost
                            what they sent. `tasks/document-rejection-resend.md`.
                        -->
                        <p *ngIf="!documentRequest.description && !documentRequest.resubmission">
                            There are two steps: download the document, then send the completed pages
                            back here.
                        </p>
                    </div>

                    <!--
                        SENT BACK - the reason, first, before anything they have to do.

                        It is the only part of this step the patient has not seen before, and it is
                        the part that tells them what to do differently. The note is not optional at
                        the other end: a rejection without one is refused, precisely so there is
                        something to put here.
                    -->
                    <div class="doc-returned-note" *ngIf="documentRequest.resubmission && !documentRequest.returned"
                        data-testid="doc-sent-back">
                        <div class="doc-returned-note-head">
                            <mat-icon>replay</mat-icon>
                            <strong>We have sent this back to you</strong>
                        </div>
                        <p class="doc-returned-note-body" *ngIf="documentRequest.previousNote"
                            data-testid="doc-sent-back-note">
                            {{ documentRequest.previousNote }}
                        </p>
                        <p class="doc-muted">
                            Please make the change above and send it to us again. You can use the
                            copy you already have, or download a fresh one below.
                        </p>

                        <!--
                            WHAT THEY SENT LAST TIME, on demand.

                            Not fetched on load: the links expire in minutes and most people will
                            not open them. Hidden entirely when there is nothing to show, rather
                            than offering a button that reveals an empty list.
                        -->
                        <ng-container *ngIf="documentRequest.previousFileCount">
                            <button mat-button type="button" class="doc-previous-toggle"
                                (click)="togglePreviousAttempt(documentRequest)"
                                data-testid="doc-previous-toggle">
                                <mat-icon>{{ documentRequest.previousOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
                                {{ documentRequest.previousOpen ? 'Hide' : 'See' }} what you sent us
                                ({{ documentRequest.previousFileCount }}
                                {{ documentRequest.previousFileCount === 1 ? 'page' : 'pages' }})
                            </button>

                            <div class="doc-previous" *ngIf="documentRequest.previousOpen"
                                data-testid="doc-previous">
                                <p class="doc-muted" *ngIf="documentRequest.previousLoading">Loading…</p>
                                <p class="doc-error" *ngIf="documentRequest.previousError" role="alert">
                                    {{ documentRequest.previousError }}
                                </p>
                                <ul class="doc-previous-list" *ngIf="documentRequest.previousFiles.length">
                                    <li *ngFor="let file of documentRequest.previousFiles; let i = index">
                                        <a *ngIf="file.url" [href]="file.url" target="_blank" rel="noopener">
                                            {{ previousFileLabel(file, i) }}
                                        </a>
                                        <!-- A page that exists and cannot be opened is a different
                                             fact from a page that was never there. -->
                                        <span *ngIf="!file.url" class="doc-muted">
                                            {{ previousFileLabel(file, i) }} — this page cannot be
                                            opened.
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </ng-container>
                    </div>

                    <!-- Already returned. Shown INSTEAD of the two steps, because there is nothing
                         further for the patient to do and a live download link on a finished
                         document is a link that will stop working. `alreadyReturned` arrives here
                         too - it means the pages are filed, which is a success. -->
                    <div class="doc-returned" *ngIf="documentRequest.returned" data-testid="doc-returned">
                        <mat-icon class="doc-returned-icon">check_circle</mat-icon>
                        <p class="doc-returned-message">{{ documentRequest.returnedMessage }}</p>
                        <p class="doc-muted">
                            There is nothing further you need to do for this document.
                        </p>
                    </div>

                    <ng-container *ngIf="!documentRequest.returned">

                        <!-- TIER A — fill it in on screen. `tasks/practice-documents.md` §5.

                             ADDED ABOVE THE TWO STEPS, NEVER INSTEAD OF THEM. Everything below
                             this block is exactly what a tier-C document renders and is exactly
                             what it rendered before Phase 5 — the download link, the picker, the
                             caps, the list, Send. That is what makes the fallback structural: if
                             the viewer cannot open the document, this one block disappears and a
                             complete, working step is already underneath it. It is also why §11's
                             "the path must not switch under a patient" does not bite — a patient
                             who printed the blank yesterday finishes the way they always would.

                             It renders for a tier-C document NEVER: `canFillOnScreen` is false
                             the moment `fillMode` is not 'embedded'. -->
                        <section class="doc-step doc-step--fill" *ngIf="canFillOnScreen(documentRequest)"
                            data-testid="doc-fill-step">
                            <h3 class="doc-step-title">
                                <span class="doc-step-number doc-fill-badge"><mat-icon>edit</mat-icon></span>
                                Fill it in on screen
                            </h3>
                            <p class="doc-step-body">
                                You can complete this document here on your phone. No printer, no
                                scanner — type into the boxes and send it straight back.
                            </p>
                            <button mat-flat-button color="primary" type="button" class="doc-fill-open"
                                (click)="openFillDialog(documentRequest)" data-testid="doc-fill-open">
                                <mat-icon>edit_note</mat-icon>
                                Open and fill in {{ documentRequest.name }}
                            </button>
                            <p class="doc-muted doc-fill-alt">
                                Would you rather print it? The two steps below still work.
                            </p>
                        </section>

                        <!-- Tier A, blank not minted yet. A silent gap where a button is about to
                             appear reads as a broken screen; this says what is happening. -->
                        <p class="doc-muted" *ngIf="isPreparingFillOnScreen(documentRequest)"
                            data-testid="doc-fill-preparing">
                            Preparing the on-screen version of this document…
                        </p>

                        <!-- THE FALLBACK, SAID OUT LOUD. A document that claimed it could be
                             filled on screen and then could not must say so — a button that
                             quietly vanishes leaves the patient wondering what they did. The
                             sentence differs by reason (`fillUnavailableMessage`), because
                             "it has no boxes" and "we could not open it" ask different things
                             of them. -->
                        <p class="doc-error doc-fill-unavailable"
                            *ngIf="documentRequest.embeddedUnavailableReason" role="alert"
                            data-testid="doc-fill-unavailable">
                            {{ fillUnavailableMessage(documentRequest) }}
                        </p>

                        <!-- Step 1: the blank.
                             A PLAIN ANCHOR, not a scripted open: a native navigation is never eaten
                             by a mobile popup blocker, which a fetch-then-`window.open` would be.
                             The URL behind it is short-lived and is re-minted in the background for
                             as long as this step is the one on screen. -->
                        <section class="doc-step" data-testid="doc-download-step">
                            <h3 class="doc-step-title"><span class="doc-step-number">1</span> Download the document</h3>
                            <p class="doc-step-body">
                                Open it, print it or fill it in, and sign anywhere it asks you to.
                            </p>

                            <a class="doc-download" mat-flat-button color="primary" *ngIf="documentRequest.blankUrl"
                                [attr.href]="documentRequest.blankUrl" target="_blank" rel="noopener"
                                data-testid="doc-download">
                                <mat-icon>download</mat-icon>
                                Download {{ documentRequest.fileName || 'the document' }}
                            </a>

                            <p class="doc-muted" *ngIf="documentPageCountLabel(documentRequest)">
                                {{ documentPageCountLabel(documentRequest) }}
                            </p>

                            <p class="doc-muted" *ngIf="!documentRequest.blankUrl && documentRequest.blankLoading"
                                data-testid="doc-blank-loading">
                                Preparing your document…
                            </p>

                            <p class="doc-error" *ngIf="!documentRequest.blankUrl && documentRequest.blankError"
                                role="alert" data-testid="doc-blank-error">
                                {{ documentRequest.blankError }}
                                <button mat-button type="button" (click)="retryDocumentBlank(documentRequest)"
                                    data-testid="doc-blank-retry">Try again</button>
                            </p>
                        </section>

                        <!-- Step 2: the return.
                             SEVERAL FILES, because the realistic return is a photograph of each
                             page. The list is shown, one can be removed before sending, and a
                             failure keeps every one of them. -->
                        <section class="doc-step" data-testid="doc-upload-step">
                            <h3 class="doc-step-title"><span class="doc-step-number">2</span> Send the completed pages back</h3>
                            <p class="doc-step-body">
                                Take a photo of every page, or upload a scan. You can send more than one file.
                            </p>

                            <!-- THE PRACTICE'S OWN LIMITS, as the server published them. Said up
                                 front, because "10 files maximum" after a failed upload on a phone
                                 connection arrives too late to be useful. -->
                            <p class="doc-limits doc-muted" data-testid="doc-limits">
                                <span *ngIf="documentRequest.accepts">You can send {{ documentRequest.accepts }}. </span>
                                <span *ngIf="documentRequest.maxFiles">Up to {{ documentRequest.maxFiles }} files</span><span
                                    *ngIf="documentRequest.maxFiles && documentRequest.maxTotalBytes">, </span><span
                                    *ngIf="documentRequest.maxTotalBytes">{{ bytesLabel(documentRequest.maxTotalBytes) }} in total</span><span
                                    *ngIf="documentRequest.maxFiles || documentRequest.maxTotalBytes">.</span>
                            </p>

                            <!-- THE HOUSE PICKER, not a bare file input. Drag and drop, paste and
                                 CAMERA — the last of which is the point on a phone in a waiting
                                 room, which is what §5 says this step is for.

                                 `showGallery` is FALSE and `previewEnabled` is left off: the list
                                 below is ours and already shows each page with its size and a
                                 remove button, and it is the list the caps are counted over. Two
                                 lists would disagree the first time one was removed. It also keeps
                                 the component off any preview path, which matters because the most
                                 likely return here is HEIC from an iPhone.

                                 `allowedFileTypes` is a CONVENIENCE, never the check — the server
                                 sniffs magic bytes and `addDocumentFiles` re-checks here. -->
                            <app-file-upload class="doc-file-picker"
                                [fileObjects]="documentRequest.pickerObjects"
                                [allowMultiple]="true"
                                [showGallery]="false"
                                [cameraEnabled]="true"
                                [pasteEnabled]="true"
                                [allowedFileTypes]="acceptedReturnTypes"
                                title=""
                                hintMessage="Drag your pages here, take a photo of each one, or choose files"
                                (fileUpload)="onDocumentFileObjects(documentRequest, $event)"
                                data-testid="doc-file-input">
                            </app-file-upload>

                            <p class="doc-muted" *ngIf="!documentRequest.files.length" data-testid="doc-no-files">
                                No pages chosen yet.
                            </p>

                            <ul class="doc-file-list" *ngIf="documentRequest.files.length" data-testid="doc-file-list">
                                <li class="doc-file"
                                    *ngFor="let file of documentRequest.files; let i = index; trackBy: trackDocumentFile"
                                    data-testid="doc-file">
                                    <mat-icon class="doc-file-icon">{{ fileIcon(file) }}</mat-icon>
                                    <span class="doc-file-name">{{ file.name }}</span>
                                    <span class="doc-file-size doc-muted">{{ fileSizeLabel(file) }}</span>
                                    <button type="button" class="doc-file-remove"
                                        [disabled]="documentRequest.isUploading"
                                        (click)="removeDocumentFile(documentRequest, i)"
                                        [attr.aria-label]="'Remove ' + file.name" data-testid="doc-remove-file">
                                        <mat-icon>close</mat-icon>
                                    </button>
                                </li>
                            </ul>

                            <!-- A file we will not send, NAMED. Silently dropping it would leave the
                                 patient believing they had sent a page they had not. -->
                            <p class="doc-error" *ngIf="documentRequest.rejectedFileNames.length" role="alert"
                                data-testid="doc-rejected-files">
                                We can only take photos and PDFs, so
                                <span *ngFor="let name of documentRequest.rejectedFileNames; trackBy: trackRejectedName">{{ name }} </span>
                                was not added.
                            </p>

                            <p class="doc-error" *ngIf="documentRequest.limitError" role="alert"
                                data-testid="doc-limit-error">
                                {{ documentRequest.limitError }}
                            </p>

                            <p class="doc-muted" *ngIf="documentRequest.files.length" data-testid="doc-total">
                                {{ documentRequest.files.length }} file{{ documentRequest.files.length === 1 ? '' : 's' }} chosen,
                                {{ bytesLabel(documentTotalBytes(documentRequest)) }} in total.
                            </p>

                            <!-- Progress. A ten-page scan is megabytes on a phone connection, and an
                                 upload with no progress is one the patient abandons and restarts. -->
                            <div class="doc-progress" *ngIf="documentRequest.isUploading" data-testid="doc-progress"
                                aria-live="polite">
                                <mat-progress-bar
                                    [mode]="documentRequest.uploadPercent === null ? 'indeterminate' : 'determinate'"
                                    [value]="documentRequest.uploadPercent === null ? 0 : documentRequest.uploadPercent"></mat-progress-bar>
                                <p class="doc-muted">
                                    Sending your {{ documentRequest.files.length === 1 ? 'page' : documentRequest.files.length + ' pages' }}<span
                                        *ngIf="documentRequest.uploadPercent !== null"> — {{ documentRequest.uploadPercent }}%</span>.
                                    Please keep this page open.
                                </p>
                            </div>

                            <p class="doc-error" *ngIf="documentRequest.uploadError" role="alert"
                                data-testid="doc-upload-error">
                                {{ documentRequest.uploadError }} Your pages are still here — nothing has been lost.
                            </p>

                            <button mat-flat-button color="primary" type="button" class="doc-send"
                                [disabled]="!canUploadDocument(documentRequest)" (click)="uploadDocument(documentRequest)"
                                data-testid="doc-send">
                                {{ documentRequest.isUploading ? 'Sending…' : (documentRequest.uploadError ? 'Try again' : 'Send to the practice') }}
                            </button>

                            <p class="doc-muted" *ngIf="!documentRequest.requiredInInvite" data-testid="doc-optional">
                                You can carry on without this and send it later if you need to.
                            </p>
                        </section>

                    </ng-container>

                    <div class="wizard-actions">
                        <button mat-button (click)="previousStep()" class="back-btn">
                            <mat-icon>arrow_back</mat-icon>
                            Back
                        </button>
                        <span class="step-blocked-note" *ngIf="!isStepValid(currentStep)">
                            {{ outstandingDocumentMessage }}
                        </span>
                        <!-- NO `[disabled]`, matching the practice-form steps immediately above and
                             deliberately NOT the consent step. `nextStep()` refuses and says why;
                             on a phone a dead button is a dead end with no feedback, and the gate
                             that actually holds is `isStepValid` - which `nextStep`,
                             `canNavigateToStep` and `submit` all consult (lessons.md §4: a disabled
                             control is not an enforcement point). -->
                        <button mat-raised-button color="primary" *ngIf="!showsFinishButton"
                            (click)="nextStep()" class="next-btn">
                            Next
                            <mat-icon>arrow_forward</mat-icon>
                        </button>
                        <!--
                            The LAST step of a document-only invite (§21.2). There is no signature
                            step behind it to carry the submit button, so it carries it here — and
                            like Next it is NOT `[disabled]`: `submit()` sweeps back to the
                            outstanding document and says what is missing, which a dead button
                            cannot do (lessons.md §4).
                        -->
                        <button mat-raised-button color="primary" *ngIf="showsFinishButton"
                            (click)="submit()" class="next-btn" data-testid="document-only-finish">
                            Finish
                            <mat-icon>check</mat-icon>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Signature Step. `currentStepId`, not an index: the practice-form steps above are
                 spliced in ahead of it, so a literal 7 stops meaning "signature". -->
            <div *ngIf="currentStepId === 'signature' && !showCompleted"
                class="step-content">
                <div class="step-card">
                    <div class="step-header">
                        <h2>Digital Signature</h2>
                        <p>Please provide your signature to complete the process</p>
                    </div>

                    <div class="signature-section">
                        <p *ngIf="!consentOnly" class="declaration-text">
                            I declare that the information I have provided is true and accurate to the best of my
                            knowledge.
                            I consent to MedicAI storing and processing my information as described in its privacy
                            policy.
                        </p>

                        <p class="signature-instruction" *ngIf="!signatureForm.get('signature')?.value">
                            Please sign on the screen, inside the box below.
                        </p>

                        <div class="signature-pad-container">
                            <app-signature-pad [emitOnDragEnd]="true" format="base64" (done)="getSignatureData($event)"
                                (cleared)="getSignatureData('')" [signatoryRequired]="true">
                            </app-signature-pad>
                        </div>

                        <p class="terms-message" *ngIf="!consentOnly">
                            Your personal information is kept confidential and used solely for providing your details to
                            your
                            authorized healthcare provider. By submitting your information, you agree to our
                            <a href="assets/documents/IQject Terms of service - v0.5.pdf" target="_blank">terms &
                                conditions</a>
                            and <a href="assets/documents/IQject privacy notice - 21 January 2025.pdf"
                                target="_blank">privacy policy</a>.
                        </p>
                    </div>

                    <div class="wizard-actions">
                        <button mat-button (click)="previousStep()" class="back-btn">
                            <mat-icon>arrow_back</mat-icon>
                            Back
                        </button>
                        <span class="step-blocked-note"
                            *ngIf="!isSubmitting && (!signatureForm.get('signature')?.value || !isSignaturePadValid())">
                            {{ incompleteStepMessage }}
                        </span>
                        <button mat-raised-button [color]="formIsValid() ? 'primary' : 'warn'" (click)="submit()"
                            [disabled]="isSubmitting || !signatureForm.get('signature')?.value || !isSignaturePadValid()" class="submit-btn">
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            <!-- Completion Step -->
            <div *ngIf="showCompleted" class="step-content">
                <!-- Success State -->
                <div *ngIf="!submissionError" class="completion-card">
                    <div class="completion-icon">
                        <mat-icon>check_circle</mat-icon>
                    </div>
                    <h1>{{ alreadySubmitted ? 'Already Received, ' : 'Thank You, ' }}{{ inviteName }}</h1>

                    <ng-container *ngIf="!consentOnly && !documentOnly">
                        <ng-container *ngIf="!alreadySubmitted">
                            <p>Your information has been successfully submitted and securely stored.</p>
                        </ng-container>
                        <ng-container *ngIf="alreadySubmitted">
                            <p>We already have your information — it was submitted successfully, so nothing has been recorded twice.</p>
                        </ng-container>
                        <p>You can now safely close this window.</p>
                        <p>A member of our team will contact you if we need any additional information.</p>
                    </ng-container>

                    <!--
                        §21.2. "Your information" would be wrong here: a document-only invite never
                        asked for any. What the patient did was send documents back, and the pages
                        landed on their own step before this screen was reached (§20.2).
                    -->
                    <ng-container *ngIf="documentOnly">
                        <ng-container *ngIf="!alreadySubmitted">
                            <p>Your documents have been received and securely stored.</p>
                        </ng-container>
                        <ng-container *ngIf="alreadySubmitted">
                            <p>We already have your documents — nothing has been recorded twice.</p>
                        </ng-container>
                        <p>You can now safely close this window.</p>
                        <p>A member of our team will contact you if we need anything further.</p>
                    </ng-container>

                    <ng-container *ngIf="consentOnly">
                        <ng-container *ngIf="!alreadySubmitted">
                            <p>Your consent has been successfully captured and securely stored.</p>
                        </ng-container>
                        <ng-container *ngIf="alreadySubmitted">
                            <p>We already have your consent — it was captured successfully, so nothing has been recorded twice.</p>
                        </ng-container>
                        <p>You can now safely close this window.</p>
                        <p>A member of our team will contact you if we need any additional information or consent.</p>
                    </ng-container>

                    <!--
                        Optional Referral Source Question.

                        NOT on a document-only invite: "how did you hear about our practice?" is a
                        question for somebody joining, and a document-only invite is a manual send
                        to a patient the practice already has on file (§20.3 requirement 10).
                    -->
                    <div class="referral-source-section"
                        *ngIf="!submissionError && mainSubmissionComplete && !alreadySubmitted && !documentOnly">
                        <h3>Optional: How did you hear about our practice?</h3>
                        <p class="referral-description">This information helps us understand how patients find our practice.</p>
                        
                        <form [formGroup]="referralSourceForm" class="referral-form">
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Select an option (optional)</mat-label>
                                <mat-select formControlName="whereDidYouHearAboutUs">
                                    <mat-option value="">-- Please select --</mat-option>
                                    <mat-option *ngFor="let source of referralSources" [value]="source">
                                        {{ source }}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>
                            
                            <div class="referral-actions">
                                <button mat-button type="button" (click)="skipReferralSource()" class="skip-btn">
                                    Skip
                                </button>
                                <button mat-raised-button color="primary" type="button" (click)="submitReferralSource()" 
                                        [disabled]="isSubmitting || !referralSourceForm.get('whereDidYouHearAboutUs')?.value" class="submit-referral-btn">
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>

                    <div class="completion-actions" *ngIf="!mainSubmissionComplete || submissionError">
                        <button mat-raised-button color="primary" (click)="closeWindow()" class="success-btn">
                            Close Window
                        </button>
                    </div>
                </div>

                <!-- Error State -->
                <div *ngIf="submissionError" class="error-card">
                    <div class="error-icon">
                        <mat-icon>error</mat-icon>
                    </div>
                    <h2>Submission Failed</h2>
                    <p>{{ submissionErrorMessage }}</p>
                    <div class="error-actions">
                        <button mat-raised-button color="primary" (click)="retrySubmission()" class="retry-btn">
                            <mat-icon>refresh</mat-icon>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>

        </ng-container>
    </div>
</div>

<!-- Loading Template -->
<ng-template #loading>
    <div class="loading-container" *ngIf="!errorText; else error;">
        <div class="loading-card">
            <mat-spinner diameter="60"></mat-spinner>
            <h2>Loading Your Invitation</h2>
            <p>Please wait while we retrieve your details...</p>
        </div>
    </div>
</ng-template>

<!-- Error Template -->
<ng-template #error>
    <div class="error-container">
        <div class="error-card">
            <div class="error-icon" [class.completed-icon]="inviteStatus === 'completed'">
                <mat-icon>{{ inviteStatus === 'completed' ? 'check_circle' : 'error' }}</mat-icon>
            </div>
            <h2>{{ errorHeading }}</h2>
            <p>{{ errorText }}</p>
            <button mat-raised-button color="primary" (click)="reloadPage()" class="retry-btn"
                *ngIf="canRetryInviteLoad">
                Try Again
            </button>
        </div>
    </div>
</ng-template>

<!-- Fullscreen Consent Dialog -->
<ng-template #fullScreenDialog>
    <h1 mat-dialog-title>{{ fullScreenConsentForm.consentLabel }}</h1>
    <div mat-dialog-content>
        <pre [innerHtml]="fullScreenConsentForm.consentText" class="consent-text no-height-limit"></pre>
        <ng-container *ngIf="fullScreenConsentForm.form.consentType === 'interactive'">
            <p class="p-t-10 align-left">
                Based on the information and statements provided, I acknowledge that my doctor has thoroughly
                explained the procedure to me, answered all my questions, and I hereby request and consent to the
                treatment.
            </p>
        </ng-container>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" mat-dialog-close>Close</button>
    </div>
</ng-template>